/**
 * Gallery scene: index chrome SoT (?test=maater-ui#index-chrome)
 */
import { ChevronDown, MoreVertical, Search, SlidersHorizontal } from 'lucide-react'
import { ExtensionFilterBar } from '@/components/integrations/_shared/extension-filter-bar'
import { IndexSurfaceShell } from '@/components/integrations/_shared/index-surface-shell'
import { PeekHeader } from '@/components/layout/shell'
import { AddressBar } from '@/components/ui/address-bar'
import { Button } from '@/components/ui/button'
import { IconButton } from '@/components/ui/icon-button'
import { Input } from '@/components/ui/input'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { testIds } from '@/lib/test-ids'

function TodosTitleRowDemo() {
  return (
    <nav className="flex items-center gap-2 px-3 py-2 border-b border-border bg-background/50">
      <NavigationButtons canGoBack canGoForward={false} />
      <div className="min-w-0 flex-1">
        <AddressBar segments={[{ label: 'Todos', path: null }]} containerWidth={null} />
      </div>
      <IconButton icon={<MoreVertical className="h-4 w-4" />} label="Options" variant="ghost" size="sm" />
    </nav>
  )
}

function SearchTitleRowDemo({ narrow }: { narrow?: boolean }) {
  return (
    <nav className="flex items-center gap-2 px-3 py-2 border-b border-border bg-background/50">
      <NavigationButtons canGoBack canGoForward={false} />
      <div className="relative min-w-[8rem] flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          readOnly
          value={narrow ? 'launch' : 'launch checklist'}
          className="h-7 pl-8 text-xs"
        />
      </div>
      <IconButton icon={<MoreVertical className="h-4 w-4" />} label="Options" variant="ghost" size="sm" />
    </nav>
  )
}

export function IndexChromeScene() {
  return (
    <div className="space-y-6" data-testid={testIds.maaterUiGallery.indexChrome}>
      <p className="text-sm text-muted-foreground">
        <strong className="font-medium text-foreground">Basecamp Todos</strong> is the
        gold-standard index. Title row{' '}
        <code className="text-xs">[&lt; &gt; | crumb | ...]</code>, then filter row above
        the peek. Cmd+F is a second row. Footer{' '}
        <code className="text-xs">(?)</code> teaches gestures. Import{' '}
        <code className="text-xs">IndexSurfaceShell</code> +{' '}
        <code className="text-xs">ExtensionFilterBar</code>.
      </p>
      <p className="text-xs text-muted-foreground">
        <strong className="text-foreground">Search</strong> uses the same shell; the
        keyword field replaces the crumb (the query is the title). Narrow columns fold
        Scope / User into Filters. Special tabs (Home / Inbox / Search) show{' '}
        <code className="text-[10px]">&lt; &gt;</code> when they record history.
      </p>
      <p className="text-xs text-muted-foreground">
        <strong className="text-foreground">Search Activity</strong>{' '}
        <code className="text-[10px]">/search?view=activity</code> is a Search named
        view. <strong className="text-foreground">Basecamp Activity</strong>{' '}
        <code className="text-[10px]">/{'{slug}'}/basecamp/activity</code> is a limb
        index. Same noun, different hosts.
      </p>

      <div>
        <p className="mb-2 text-xs font-medium text-foreground">Todos (wide)</p>
        <div className="h-[280px] overflow-hidden rounded-md border border-border">
          <IndexSurfaceShell
            header={<TodosTitleRowDemo />}
            filter={
              <ExtensionFilterBar
                className="border-b border-border px-3 py-2"
                search=""
                onSearchChange={() => undefined}
                showSearch={false}
                chipStyle="segment"
                chips={[
                  { id: 'me', label: 'Me' },
                  { id: 'org', label: 'Org' },
                ]}
                activeChipId="me"
                trailing={
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs font-normal border-primary/50 text-primary"
                    >
                      <SlidersHorizontal className="h-3 w-3" />
                      Filters
                      <span className="rounded-full bg-primary px-1 text-[10px] leading-4 text-primary-foreground">
                        1
                      </span>
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      &quot;launch checklist&quot; - 0 of 105
                    </span>
                  </>
                }
              />
            }
            footer={<ListSurfaceStatusBar includePreviewTip />}
          >
            <div className="flex min-h-0 flex-1">
              <div className="min-w-0 flex-1 px-3 py-2 text-sm">Assignment rows</div>
              <div className="w-[38%] shrink-0 border-l border-border">
                <PeekHeader
                  chips={<span className="text-xs">Hop Business</span>}
                  onClose={() => undefined}
                />
                <p className="px-3 py-2 text-xs text-muted-foreground">Peek body. No type stamp.</p>
              </div>
            </div>
          </IndexSurfaceShell>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium text-foreground">
          Search (narrow ~420px - Scope/User folded into Filters)
        </p>
        <div className="h-[240px] w-[420px] max-w-full overflow-hidden rounded-md border border-border">
          <IndexSurfaceShell
            header={<SearchTitleRowDemo narrow />}
            filter={
              <ExtensionFilterBar
                className="border-b border-border px-3 py-2"
                search=""
                onSearchChange={() => undefined}
                showSearch={false}
                chipStyle="segment"
                chips={[
                  { id: 'search', label: 'Search' },
                  { id: 'activity', label: 'Activity' },
                ]}
                activeChipId="search"
                trailing={
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs font-normal border-primary/50 text-primary"
                    >
                      <SlidersHorizontal className="h-3 w-3" />
                      Filters
                      <span className="rounded-full bg-primary px-1 text-[10px] leading-4 text-primary-foreground">
                        2
                      </span>
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                    <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                      20 results
                    </span>
                  </>
                }
              />
            }
            footer={<ListSurfaceStatusBar includePreviewTip />}
          >
            <div className="px-3 py-2 text-sm text-muted-foreground">Result cards</div>
          </IndexSurfaceShell>
        </div>
      </div>
    </div>
  )
}
